import { useState, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TextInput,
  Select,
  Pagination,
  Checkbox
} from 'flowbite-react';
import { HiSearch, HiChevronUp, HiChevronDown, HiSelector } from 'react-icons/hi';

// Column definition interface
export interface ColumnDef<T> {
  id: string;
  header: string;
  accessor: keyof T | ((row: T) => any);
  sortable?: boolean;
  searchable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  nowrap?: boolean; // defaults to true
  render?: (value: any, row: T) => React.ReactNode;
}

// DataTable props
export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  searchable?: boolean;
  searchPlaceholder?: string;
  sortable?: boolean;
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  selectable?: boolean;
  onRowClick?: (row: T, event: React.MouseEvent) => void;
  onSelectionChange?: (selectedRows: T[]) => void;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
  striped?: boolean;
  hoverable?: boolean;
  caption?: string;
  ariaLabel?: string;
  // Server-side pagination props
  serverSide?: boolean;  // Enable server-side pagination mode
  totalItems?: number;   // Total count from server (required when serverSide=true)
  currentPage?: number;  // Controlled page (for server-side)
  onPageChange?: (page: number, pageSize: number) => void;  // Callback when page changes
}

// Sort direction type
type SortDirection = 'asc' | 'desc' | null;

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  searchable = true,
  searchPlaceholder = 'Search...',
  sortable = true,
  pagination = true,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [10, 25, 50, 100],
  selectable = false,
  onRowClick,
  onSelectionChange,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  striped = true,
  hoverable = true,
  caption,
  ariaLabel,
  // Server-side pagination props
  serverSide = false,
  totalItems,
  currentPage: controlledPage,
  onPageChange
}: DataTableProps<T>) {
  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [internalPage, setInternalPage] = useState(1);
  const [currentPageSize, setPageSize] = useState(initialPageSize);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Use controlled page if provided, otherwise use internal state
  const currentPage = controlledPage ?? internalPage;
  const setCurrentPage = (page: number) => {
    if (onPageChange) {
      onPageChange(page, currentPageSize);
    }
    if (!controlledPage) {
      setInternalPage(page);
    }
  };

  // Get value from accessor
  const getValue = useCallback((row: T, accessor: keyof T | ((row: T) => any)) => {
    if (typeof accessor === 'function') {
      return accessor(row);
    }
    return row[accessor];
  }, []);

  // Filter data based on search term (client-side only)
  const filteredData = useMemo(() => {
    // Skip client-side filtering in server-side mode
    if (serverSide || !searchTerm || !searchable) return data;

    const searchColumns = columns.filter(col => col.searchable !== false);
    const lowerSearchTerm = searchTerm.toLowerCase();

    return data.filter(row => {
      return searchColumns.some(col => {
        const value = getValue(row, col.accessor);
        if (value == null) return false;
        return String(value).toLowerCase().includes(lowerSearchTerm);
      });
    });
  }, [data, searchTerm, searchable, columns, getValue, serverSide]);

  // Sort data (client-side only)
  const sortedData = useMemo(() => {
    // Skip client-side sorting in server-side mode
    if (serverSide || !sortColumn || !sortDirection || !sortable) return filteredData;

    const column = columns.find(col => col.id === sortColumn);
    if (!column) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = getValue(a, column.accessor);
      const bValue = getValue(b, column.accessor);

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortColumn, sortDirection, sortable, columns, getValue, serverSide]);

  // Paginate data (client-side only)
  const paginatedData = useMemo(() => {
    // In server-side mode, data is already paginated by the server
    if (serverSide || !pagination) return sortedData;

    const startIndex = (currentPage - 1) * currentPageSize;
    const endIndex = startIndex + currentPageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, currentPageSize, pagination, serverSide]);

  // Total pages calculation - use totalItems when in server-side mode
  const effectiveTotalItems = serverSide ? (totalItems ?? data.length) : sortedData.length;
  const totalPages = Math.ceil(effectiveTotalItems / currentPageSize);

  // Handle sort
  const handleSort = (columnId: string) => {
    if (!sortable) return;

    const column = columns.find(col => col.id === columnId);
    if (!column || column.sortable === false) return;

    if (sortColumn === columnId) {
      setSortDirection(current => {
        if (current === 'asc') return 'desc';
        if (current === 'desc') return null;
        return 'asc';
      });
      if (sortDirection === 'desc') {
        setSortColumn(null);
      }
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  // Handle row selection
  const handleSelectAll = () => {
    if (selectedRows.size === paginatedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginatedData.map((_, index) => index)));
    }
  };

  const handleSelectRow = (index: number) => {
    const newSelection = new Set(selectedRows);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedRows(newSelection);
  };

  // Update parent when selection changes
  useMemo(() => {
    if (onSelectionChange) {
      const selected = paginatedData.filter((_, index) => selectedRows.has(index));
      onSelectionChange(selected);
    }
  }, [selectedRows, paginatedData, onSelectionChange]);

  // Render sort icon
  const renderSortIcon = (columnId: string) => {
    if (sortColumn !== columnId) {
      return <HiSelector className="w-4 h-4 text-gray-400" />;
    }
    if (sortDirection === 'asc') {
      return <HiChevronUp className="w-4 h-4 text-blue-600" />;
    }
    if (sortDirection === 'desc') {
      return <HiChevronDown className="w-4 h-4 text-blue-600" />;
    }
    return <HiSelector className="w-4 h-4 text-gray-400" />;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="spinner w-8 h-8"></div>
        <span className="ml-3 text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search and controls */}
      {(searchable || pagination) && (
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          {searchable && (
            <div className="relative flex-1 max-w-md">
              <div className="absolute top-1/2 -translate-y-1/2 left-0 flex items-center pl-3 pointer-events-none z-10">
                <HiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <TextInput
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page on search
                }}
                className="pl-10"
                aria-label="Search table"
              />
            </div>
          )}

          {pagination && (
            <div className="flex items-center gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 dark:text-gray-300">
                Show
              </label>
              <Select
                id="pageSize"
                value={currentPageSize}
                onChange={(e) => {
                  const newPageSize = Number(e.target.value);
                  setPageSize(newPageSize);
                  // Reset to page 1 and notify parent
                  if (onPageChange) {
                    onPageChange(1, newPageSize);
                  }
                  if (!controlledPage) {
                    setInternalPage(1);
                  }
                }}
                className="w-20"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
              <span className="text-sm text-gray-700 dark:text-gray-300">entries</span>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <Table
          striped={striped}
          hoverable={hoverable}
          aria-label={ariaLabel || 'Data table'}
          className="divide-y divide-gray-200 dark:divide-gray-700"
        >
          {caption && <caption className="sr-only">{caption}</caption>}

          <TableHead className="bg-gray-50 dark:bg-gray-800">
            <TableRow>
              {selectable && (
                <TableHeadCell className="w-10 px-4">
                  <div className="flex items-center">
                    <Checkbox
                      checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                      onChange={handleSelectAll}
                      aria-label="Select all rows"
                      className="w-4 h-4"
                    />
                  </div>
                </TableHeadCell>
              )}

              {columns.map(column => (
                <TableHeadCell
                  key={column.id}
                  onClick={() => handleSort(column.id)}
                  className={`
                    px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider
                    ${column.align === 'center' ? 'text-center' : ''}
                    ${column.align === 'right' ? 'text-right' : ''}
                    ${sortable && column.sortable !== false ? 'cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors' : ''}
                  `}
                  style={{ width: column.width }}
                >
                  <div className={`flex items-center gap-1 ${column.align === 'center' ? 'justify-center' : ''} ${column.align === 'right' ? 'justify-end' : ''}`}>
                    <span>{column.header}</span>
                    {sortable && column.sortable !== false && renderSortIcon(column.id)}
                  </div>
                </TableHeadCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="text-center py-8 text-gray-500 dark:text-gray-400"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  onClick={(e) => onRowClick && onRowClick(row, e)}
                  onAuxClick={(e) => {
                    if (onRowClick && e.button === 1) onRowClick(row, e);
                  }}
                  className={`
                    ${onRowClick ? 'cursor-pointer' : ''}
                    hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                    ${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50/50 dark:bg-gray-800/50'}
                  `}
                >
                  {selectable && (
                    <TableCell className="w-10 px-4">
                      <div className="flex items-center">
                        <Checkbox
                          checked={selectedRows.has(rowIndex)}
                          onChange={() => handleSelectRow(rowIndex)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select row ${rowIndex + 1}`}
                          className="w-4 h-4"
                        />
                      </div>
                    </TableCell>
                  )}

                  {columns.map(column => {
                    const value = getValue(row, column.accessor);
                    const cellContent = column.render ? column.render(value, row) : value;

                    return (
                      <TableCell
                        key={column.id}
                        className={`
                          px-6 py-4 text-sm text-gray-900 dark:text-gray-100
                          ${column.nowrap !== false ? 'whitespace-nowrap' : ''}
                          ${column.align === 'center' ? 'text-center' : ''}
                          ${column.align === 'right' ? 'text-right' : ''}
                        `}
                      >
                        {cellContent}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Showing {((currentPage - 1) * currentPageSize) + 1} to{' '}
            {Math.min(currentPage * currentPageSize, effectiveTotalItems)} of{' '}
            {effectiveTotalItems} entries
            {!serverSide && filteredData.length !== data.length && (
              <span> (filtered from {data.length} total entries)</span>
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            showIcons
            aria-label="Table pagination"
          />
        </div>
      )}
    </div>
  );
}